import { useQuery } from '@apollo/client';
import { AddCircle, OndemandVideo } from '@mui/icons-material';
import { Box } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { ButtonLink } from '~/components/link';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { ChannelListDocument } from '../queries/list.generated';
import { BulkActions, Table } from './components';

export const ChannelList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(ChannelListDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search },
    onCompleted: (data) => handleOutOfBounds(data.network?.channelPages.pageInfo),
  });

  const totalCount = useTotalCount(data?.network?.channelPages.pageInfo.nodesCount);
  const channels = data?.network?.channelPages.nodes ?? [];

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  useEffect(() => setSelectedIds([]), [setSelectedIds]);

  const canDestroySelected = selectedIds.every((selectedId) => {
    const channel = channels.find((channel) => channel.id === selectedId);
    return channel?.canDestroy.value;
  });

  if (!loading && data?.network == null) return <Navigate replace to="/not-found" />;

  return (
    <>
      <Helmet title={t('pages.channels.title')} />
      <Toolbar
        actions={
          <ButtonLink color="primary" startIcon={<AddCircle />} variant="contained" to="new">
            {t('pages.channels.new_button')}
          </ButtonLink>
        }
        titleIcon={<OndemandVideo />}
        titleText={t('pages.channels.title')}
      ></Toolbar>
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar placeholder={t('pages.channels.search_placeholder')} search={search} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedIds.length > 0 ? (
                  <BulkActions canDestroySelected={canDestroySelected} selectedIds={selectedIds} />
                ) : null}
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={totalCount}
                  thing="common.pluralize.channel_plural"
                />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table
                  channels={channels}
                  loading={loading}
                  onCheck={setSelectedIds}
                  selectedIds={selectedIds}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
    </>
  );
};
